import { useState, useEffect } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import RoleAccessManagementService from './RoleAccessManagement.service';
import './RoleAccessManagement.css';

function RoleAccessManagement() {
  const { userData, onLogout } = useOutletContext();
  const [lastLoginTime, setLastLoginTime] = useState(null);
  const [activeTab, setActiveTab] = useState('engineers');
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    mobileNo: '+91',
    emailId: '',
  });
  const [emailError, setEmailError] = useState('');
  const [addUserError, setAddUserError] = useState('');
  const [isAddingUser, setIsAddingUser] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [isDeletingUser, setIsDeletingUser] = useState(false);
  const [deleteSuccessMessage, setDeleteSuccessMessage] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [requestToast, setRequestToast] = useState({ open: false, message: '', kind: 'success' });
  const [processingRequestId, setProcessingRequestId] = useState(null);
  
  // Search states for each section
  const [engineerSearch, setEngineerSearch] = useState('');
  const [customerSearch, setCustomerSearch] = useState('');
  const [adminSearch, setAdminSearch] = useState('');
  const [requestSearch, setRequestSearch] = useState('');
  
  // Filter column states for each section
  const [engineerFilterColumn, setEngineerFilterColumn] = useState('all');
  const [customerFilterColumn, setCustomerFilterColumn] = useState('all');
  const [adminFilterColumn, setAdminFilterColumn] = useState('all');
  const [requestFilterColumn, setRequestFilterColumn] = useState('all');
  
  // Data states
  const [allEngineers, setAllEngineers] = useState([]);
  const [allCustomers, setAllCustomers] = useState([]);
  const [allCustomerRequests, setAllCustomerRequests] = useState([]);
  const [allAdmins, setAllAdmins] = useState([]);
  
  const { userDetails, dashboardList } = userData || {};
  const navigate = useNavigate();

  // Role ID constants
  // GUEST - 1, CUSTOMER - 2, ENGINEER - 3, ADMIN - 4, SUPERADMIN - 5
  
  // Get roleId (handle both string and number from API)
  // Check multiple possible paths for roleId
  const userRoleId = userDetails?.roleId || userDetails?.role?.roleId || userData?.roleId || userData?.role?.roleId;
  
  // Convert to number if it's a string, otherwise use as-is
  const roleId = userRoleId != null && userRoleId !== undefined ? Number(userRoleId) : null;
  
  // Check if user is SUPERADMIN (roleId = 5)
  // Only SUPERADMIN can add new admins
  // Using explicit number comparison to ensure type matching
  const isSuperAdmin = Number(roleId) === 5;
  
  // Check if user can view Admin tab (SUPERADMIN or ADMIN)
  // Both SUPERADMIN and ADMIN can view existing admins
  // Using explicit number comparisons to ensure type matching
  const canViewAdmins = Number(roleId) === 5 || Number(roleId) === 4;

  // Load data when tab changes or on initial mount
  useEffect(() => {
    if (activeTab === 'engineers') {
      loadEngineersData();
    } else if (activeTab === 'customers') {
      loadCustomersData();
      loadCustomerRequestsData();
    } else if (activeTab === 'admins' && canViewAdmins) {
      loadAdminsData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, canViewAdmins]);

  // Load engineers data
  const loadEngineersData = async () => {
    try {
      const engineers = await RoleAccessManagementService.getEngineers();
      setAllEngineers(engineers);
    } catch (error) {
      console.error('Error loading engineers:', error);
    }
  };

  // Load customers data
  const loadCustomersData = async () => {
    try {
      const customers = await RoleAccessManagementService.getCustomers();
      setAllCustomers(customers);
    } catch (error) {
      console.error('Error loading customers:', error);
    }
  };

  // Load customer requests data
  const loadCustomerRequestsData = async () => {
    try {
      const requests = await RoleAccessManagementService.getCustomerRequests();
      setAllCustomerRequests(requests);
    } catch (error) {
      console.error('Error loading customer requests:', error);
    }
  };

  // Load admins data
  const loadAdminsData = async () => {
    try {
      const admins = await RoleAccessManagementService.getAdmins();
      setAllAdmins(admins);
    } catch (error) {
      console.error('Error loading admins:', error);
    }
  };

  // Load all data (used for refreshing after add/delete/approve/reject operations)
  const loadAllData = async () => {
    try {
      if (activeTab === 'engineers') {
        await loadEngineersData();
      } else if (activeTab === 'customers') {
        await Promise.all([
          loadCustomersData(),
          loadCustomerRequestsData(),
        ]);
      } else if (activeTab === 'admins') {
        await loadAdminsData();
      }
    } catch (error) {
      console.error('Error loading data:', error);
    }
  };

  useEffect(() => {
    // Get last login time from localStorage
    const stored = localStorage.getItem('lastLoginTime');
    if (stored) {
      setLastLoginTime(new Date(stored).toLocaleString());
    } else {
      const now = new Date();
      localStorage.setItem('lastLoginTime', now.toISOString());
      setLastLoginTime(now.toLocaleString());
    }
  }, []);

  // Reset add form when switching to admins tab and user is not SUPERADMIN
  useEffect(() => {
    if (activeTab === 'admins' && !isSuperAdmin) {
      setShowAddForm(false);
    }
  }, [activeTab, isSuperAdmin]);

  useEffect(() => {
    if (!requestToast.open) return undefined;
    const timer = setTimeout(() => {
      setRequestToast((prev) => ({ ...prev, open: false }));
    }, 3000);
    return () => clearTimeout(timer);
  }, [requestToast.open]);

  // Email validation function with strict format check
  // Format: characters@characters.characters (e.g., user@domain.com)
  const validateEmail = (email) => {
    if (!email || email.trim() === '') {
      return false;
    }
    
    const trimmedEmail = email.trim();
    
    // Basic length check
    if (trimmedEmail.length > 254 || trimmedEmail.length < 5) {
      return false;
    }
    
    // Must contain exactly one @ symbol
    const atIndex = trimmedEmail.indexOf('@');
    if (atIndex === -1 || trimmedEmail.indexOf('@', atIndex + 1) !== -1) {
      return false;
    }
    
    // Split into local (before @) and domain (after @) parts
    const localPart = trimmedEmail.substring(0, atIndex);
    const domainPart = trimmedEmail.substring(atIndex + 1);
    
    // Local part validation (before @) - must have at least one character
    if (!localPart || localPart.length === 0 || localPart.length > 64) {
      return false;
    }
    
    // Local part must contain at least one valid character (alphanumeric)
    if (!/^[a-zA-Z0-9]/.test(localPart)) {
      return false;
    }
    
    // Local part should contain valid characters
    if (!/^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+$/.test(localPart)) {
      return false;
    }
    
    // Domain part validation (after @) - must have at least one character
    if (!domainPart || domainPart.length === 0) {
      return false;
    }
    
    // Domain must contain at least one dot (.)
    if (!domainPart.includes('.')) {
      return false;
    }
    
    // Split domain into parts by dot
    const domainParts = domainPart.split('.');
    
    // Must have at least 2 parts (domain name and TLD like .com, .org)
    if (domainParts.length < 2) {
      return false;
    }
    
    // Check that there's at least one character before the first dot
    const domainName = domainParts[0];
    if (!domainName || domainName.length === 0) {
      return false;
    }
    
    // Domain name must start with alphanumeric character
    if (!/^[a-zA-Z0-9]/.test(domainName)) {
      return false;
    }
    
    // Last part (TLD like .com, .org) must be at least 2 characters and contain only letters
    const tld = domainParts[domainParts.length - 1];
    if (!tld || tld.length < 2 || !/^[a-zA-Z]{2,}$/.test(tld)) {
      return false;
    }
    
    // Each domain part (between dots) must be valid
    for (let i = 0; i < domainParts.length; i++) {
      const part = domainParts[i];
      
      // Each part must not be empty
      if (!part || part.length === 0) {
        return false;
      }
      
      // Each part (except TLD) can contain letters, numbers, and hyphens
      if (i < domainParts.length - 1) {
        if (!/^[a-zA-Z0-9-]+$/.test(part)) {
          return false;
        }
        // Cannot start or end with hyphen
        if (part.startsWith('-') || part.endsWith('-')) {
          return false;
        }
      }
    }
    
    // Final strict regex check: format must be characters@characters.characters
    // Pattern: at least one char before @, at least one char after @, dot, at least 2 chars after dot
    const strictEmailRegex = /^[a-zA-Z0-9][a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]*@[a-zA-Z0-9][a-zA-Z0-9.-]*\.[a-zA-Z]{2,}$/;
    return strictEmailRegex.test(trimmedEmail);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Handle email validation
    if (name === 'emailId') {
      setEmailError('');
      if (value && !validateEmail(value)) {
        setEmailError('Please enter a valid email address (format: user@domain.com)');
      }
    }
    
    // Handle mobile number - ensure it always starts with +91
    if (name === 'mobileNo') {
      // If empty, set to +91
      if (!value || value.trim() === '') {
        setFormData((prev) => ({
          ...prev,
          [name]: '+91',
        }));
        return;
      }
      
      // If user tries to delete +91, keep it
      if (value.length < 3) {
        setFormData((prev) => ({
          ...prev,
          [name]: '+91',
        }));
        return;
      }
      
      // If value doesn't start with +91, prepend it
      if (!value.startsWith('+91')) {
        // Remove any leading + or digits, then add +91
        const cleanedValue = value.replace(/^\+?91?/, '').replace(/^\+/, '');
        setFormData((prev) => ({
          ...prev,
          [name]: '+91' + cleanedValue,
        }));
        return;
      }
      
      // Ensure only digits after +91
      const afterPrefix = value.substring(3);
      if (afterPrefix && !/^\d*$/.test(afterPrefix)) {
        // Remove non-digits
        const digitsOnly = afterPrefix.replace(/\D/g, '');
        setFormData((prev) => ({
          ...prev,
          [name]: '+91' + digitsOnly,
        }));
        return;
      }
      
      // Limit to 10 digits after +91
      if (afterPrefix && afterPrefix.length > 10) {
        setFormData((prev) => ({
          ...prev,
          [name]: '+91' + afterPrefix.substring(0, 10),
        }));
        return;
      }
    }
    
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    setAddUserError('');
    setEmailError('');
    setIsAddingUser(true);
    
    // Validate email before submission
    if (!validateEmail(formData.emailId)) {
      setEmailError('Please enter a valid email address (format: user@domain.com)');
      setIsAddingUser(false);
      return;
    }
    
    // Validate mobile number starts with +91
    if (!formData.mobileNo.startsWith('+91') || formData.mobileNo.length < 13) {
      setAddUserError('Please enter a valid mobile number with country code (+91)');
      setIsAddingUser(false);
      return;
    }
    
    try {
      await RoleAccessManagementService.addUser(formData, activeTab);
      // Reload data after adding user
      await loadAllData();
      // Reset form
      setFormData({ username: '', mobileNo: '+91', emailId: '' });
      setEmailError('');
      setShowAddForm(false);
    } catch (error) {
      console.error('Error adding user:', error);
      // Show user-friendly error message
      setAddUserError(
        error.message || 
        `Failed to add ${activeTab === 'admins' ? 'admin' : 'engineer'}. Please try again.`
      );
    } finally {
      setIsAddingUser(false);
    }
  };

  const handleApproveRequest = async (requestId) => {
    setRequestToast({ open: false, message: '', kind: 'success' });
    setProcessingRequestId(requestId);
    try {
      const result = await RoleAccessManagementService.approveCustomerRequest(requestId);
      setRequestToast({
        open: true,
        kind: 'success',
        message: result.message || 'Customer access request approved successfully',
      });
      await loadAllData();
    } catch (error) {
      console.error('Error approving request:', error);
      setRequestToast({
        open: true,
        kind: 'error',
        message: error.message || 'Failed to approve request. Please try again.',
      });
    } finally {
      setProcessingRequestId(null);
    }
  };

  const handleRejectRequest = async (requestId) => {
    setRequestToast({ open: false, message: '', kind: 'success' });
    setProcessingRequestId(requestId);
    try {
      const result = await RoleAccessManagementService.rejectCustomerRequest(requestId);
      setRequestToast({
        open: true,
        kind: 'success',
        message: result.message || 'Customer access request rejected successfully',
      });
      await loadAllData();
    } catch (error) {
      console.error('Error rejecting request:', error);
      setRequestToast({
        open: true,
        kind: 'error',
        message: error.message || 'Failed to reject request. Please try again.',
      });
    } finally {
      setProcessingRequestId(null);
    }
  };

  const handleEditUser = async (userId) => {
    try {
      // TODO: Get user data and open edit form
      // For now, just log the action
      const roleType = activeTab;
      console.log('Edit user:', userId, roleType);
      // await RoleAccessManagementService.updateUser(userId, userData, roleType);
      // await loadAllData();
    } catch (error) {
      console.error('Error editing user:', error);
      // TODO: Show error message to user
    }
  };

  const handleDeleteUser = (userId) => {
    // Find the user from the current tab's data
    const userList = activeTab === 'engineers' ? allEngineers : 
                     activeTab === 'admins' ? allAdmins : 
                     allCustomers;
    const user = userList.find(u => u.id === userId);
    
    if (user) {
      setUserToDelete({ ...user, roleType: activeTab });
      setShowDeleteConfirm(true);
      setDeleteError('');
      setDeleteSuccessMessage('');
    }
  };

  const confirmDeleteUser = async () => {
    if (!userToDelete) return;
    
    setIsDeletingUser(true);
    setDeleteError('');
    setDeleteSuccessMessage('');
    
    try {
      const roleType = userToDelete.roleType;
      if (roleType === 'customers') {
        const result = await RoleAccessManagementService.removeCustomerAccess(userToDelete.id);
        setDeleteSuccessMessage(
          result.msg || result.message || 'Customer access revoked successfully'
        );
      } else {
        await RoleAccessManagementService.deleteUser(userToDelete.id, roleType);
        const roleName = roleType === 'admins' ? 'Admin' : 'Engineer';
        setDeleteSuccessMessage(`${roleName} access removed successfully`);
      }
      
      // Close confirmation popup after a brief delay
      setTimeout(() => {
        setShowDeleteConfirm(false);
        setUserToDelete(null);
        setDeleteSuccessMessage('');
        // Reload data after deleting user
        loadAllData();
      }, 1500);
    } catch (error) {
      console.error('Error deleting user:', error);
      setDeleteError(error.message || 'Failed to remove access. Please try again.');
    } finally {
      setIsDeletingUser(false);
    }
  };

  const cancelDeleteUser = () => {
    setShowDeleteConfirm(false);
    setUserToDelete(null);
    setDeleteError('');
    setDeleteSuccessMessage('');
  };

  // Search filter: when column is 'all', search across all text columns; otherwise search in selected column only
  const filterByColumn = (items, searchQuery, filterColumn, allColumns = ['username', 'mobileNo', 'emailId']) => {
    if (!searchQuery.trim()) return items;
    const query = searchQuery.toLowerCase().trim();
    const getField = (item, col) => (col === 'company' ? (item.company ?? item.site) : item[col]);
    if (filterColumn === 'all') {
      return items.filter((item) =>
        allColumns.some((col) => (String(getField(item, col) || '')).toLowerCase().includes(query))
      );
    }
    const fieldValue = (item) => (String(getField(item, filterColumn) || '')).toLowerCase();
    return items.filter((item) => fieldValue(item).includes(query));
  };

  const engineerColumns = ['username', 'mobileNo', 'emailId'];
  const customerColumns = ['username', 'mobileNo', 'emailId', 'company'];
  const requestColumns = ['username', 'mobileNo', 'emailId', 'company'];

  const engineers = filterByColumn(allEngineers, engineerSearch, engineerFilterColumn, engineerColumns);
  const customers = filterByColumn(allCustomers, customerSearch, customerFilterColumn, customerColumns);
  const admins = filterByColumn(allAdmins, adminSearch, adminFilterColumn, engineerColumns);
  const customerRequests = filterByColumn(allCustomerRequests, requestSearch, requestFilterColumn, requestColumns);

  return (
    <div className="role-access-management-page">
      <div className="role-access-content">
        <div className="main-content-wrapper">
          {/* Tabs */}
          <div className="ram-tabs-container">
            <div className="ram-tabs">
              <button
                type="button"
                className={`ram-tab ${activeTab === 'engineers' ? 'active' : ''}`}
                onClick={() => {
                  setActiveTab('engineers');
                  setShowAddForm(false);
                }}
              >
                Engineers
              </button>
              <button
                type="button"
                className={`ram-tab ${activeTab === 'customers' ? 'active' : ''}`}
                onClick={() => {
                  setActiveTab('customers');
                  setShowAddForm(false);
                }}
              >
                Customers
              </button>
              {canViewAdmins && (
                <button
                  type="button"
                  className={`ram-tab ${activeTab === 'admins' ? 'active' : ''}`}
                  onClick={() => {
                    setActiveTab('admins');
                    setShowAddForm(false);
                  }}
                >
                  Admins
                </button>
              )}
            </div>
          </div>

          {/* Tab Content */}
          <div className="ram-tab-content">
            {/* Engineers Tab */}
            {activeTab === 'engineers' && (
              <div className="ram-tab-panel">
                <div className="ram-section-header">
                  <h2>Manage Engineers</h2>
                  <button
                    type="button"
                    className="ram-add-button"
                    onClick={() => {
                      setShowAddForm(!showAddForm);
                      setAddUserError('');
                    }}
                  >
                    {showAddForm ? 'Cancel' : '+ Add Engineer'}
                  </button>
                </div>

                {showAddForm && (
                  <div className="ram-add-form-wrap">
                  <div className="ram-form-card">
                    <div className="ram-form-card-header">
                      <div className="ram-form-icon">
                        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                          <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><line x1="19" y1="8" x2="19" y2="14"/><line x1="22" y1="11" x2="16" y1="11"/>
                        </svg>
                      </div>
                      <h3>Add New Engineer</h3>
                      <p className="ram-form-subtitle">Create a new engineer account with username, mobile and email.</p>
                    </div>
                    <form className="ram-form" onSubmit={handleAddUser}>
                      <div className="form-group">
                        <label htmlFor="username">Username <span className="required">*</span></label>
                        <input
                          type="text"
                          id="username"
                          name="username"
                          value={formData.username}
                          onChange={(e) => {
                            handleInputChange(e);
                            setAddUserError('');
                          }}
                          required
                          placeholder="Enter username"
                          disabled={isAddingUser}
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="mobileNo">Mobile Number <span className="required">*</span></label>
                        <input
                          type="tel"
                          id="mobileNo"
                          name="mobileNo"
                          value={formData.mobileNo}
                          onChange={(e) => {
                            handleInputChange(e);
                            setAddUserError('');
                          }}
                          required
                          placeholder="+919876543210"
                          disabled={isAddingUser}
                          minLength={13}
                        />
                        <span className="ram-form-hint">Format: +91 followed by 10-digit mobile number</span>
                      </div>
                      <div className="form-group">
                        <label htmlFor="emailId">Email ID <span className="required">*</span></label>
                        <input
                          type="email"
                          id="emailId"
                          name="emailId"
                          value={formData.emailId}
                          onChange={(e) => {
                            handleInputChange(e);
                            setAddUserError('');
                          }}
                          required
                          placeholder="user@example.com"
                          disabled={isAddingUser}
                        />
                        {emailError && <span className="ram-form-hint ram-form-hint-error">{emailError}</span>}
                      </div>
                      <div className="form-actions">
                        <button type="submit" className="ram-submit-button" disabled={isAddingUser}>
                          {isAddingUser ? 'Adding...' : 'Add Engineer'}
                        </button>
                        <button
                          type="button"
                          className="ram-cancel-button"
                          onClick={() => {
                            setShowAddForm(false);
                            setFormData({ username: '', mobileNo: '+91', emailId: '' });
                            setAddUserError('');
                            setEmailError('');
                          }}
                          disabled={isAddingUser}
                        >
                          Cancel
                        </button>
                      </div>
                      {addUserError && (
                        <div className="ram-error-message">
                          <span className="message-icon">✕</span>
                          <span>{addUserError}</span>
                        </div>
                      )}
                    </form>
                  </div>
                  </div>
                )}

                <div className="ram-table-section">
                  <div className="ram-table-header">
                    <h3>Engineers</h3>
                    <div className="ram-search-container">
                      <div className="ram-search-bar">
                        <select
                          className={`search-filter-select ${engineerFilterColumn === 'all' ? 'select-column-default' : ''}`}
                          value={engineerFilterColumn}
                          onChange={(e) => setEngineerFilterColumn(e.target.value)}
                          aria-label="Search in"
                        >
                          <option value="all" className="select-column-option">All columns</option>
                          <option value="username">Username</option>
                          <option value="mobileNo">Phone Number</option>
                          <option value="emailId">Email ID</option>
                        </select>
                        <div className="search-input-wrapper">
                          <input
                            type="search"
                            className="search-input"
                            placeholder={engineerFilterColumn === 'all' ? 'Search username, phone, email…' : engineerFilterColumn === 'username' ? 'Search by username…' : engineerFilterColumn === 'mobileNo' ? 'Search by phone…' : 'Search by email…'}
                            value={engineerSearch}
                            onChange={(e) => setEngineerSearch(e.target.value)}
                            aria-label="Search engineers"
                          />
                      </div>
                      </div>
                      {engineerSearch && (
                        <span className="search-result-count">
                          {engineers.length} {engineers.length === 1 ? 'result' : 'results'}
                        </span>
                      )}
                    </div>
                  </div>
                  {engineers.length > 0 ? (
                    <div className="ram-table-wrapper">
                      <table className="ram-users-table">
                        <thead>
                          <tr>
                            <th className="col-username">Username</th>
                            <th className="col-mobile">Mobile Number</th>
                            <th className="col-email">Email ID</th>
                            <th className="col-status">Status</th>
                            <th className="col-actions">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {engineers.map((engineer) => (
                            <tr key={engineer.id}>
                              <td className="col-username">{engineer.username}</td>
                              <td className="col-mobile">{engineer.mobileNo}</td>
                              <td className="col-email">{engineer.emailId}</td>
                              <td className="col-status">
                                <span className={`status-badge ${engineer.status.toLowerCase()}`}>
                                  {engineer.status}
                                </span>
                              </td>
                              <td className="col-actions">
                                <div className="action-buttons">
                                  <button
                                    className="ram-delete-button"
                                    onClick={() => handleDeleteUser(engineer.id)}
                                    title="Delete Engineer"
                                  >
                                    Delete
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="ram-empty-state">
                      <p>{engineerSearch ? `No engineers found matching "${engineerSearch}".` : 'No engineers found. Add your first engineer above.'}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Customers Tab */}
            {activeTab === 'customers' && (
              <div className="ram-tab-panel">
                <div className="ram-section-header">
                  <h2>Manage Customers</h2>
                </div>

                {/* Customer Requests Section */}
                <div className="ram-requests-section">
                  <div className="ram-table-header">
                    <h3>Access Requests</h3>
                    <div className="ram-search-container">
                      <div className="ram-search-bar">
                        <select
                          className={`search-filter-select ${requestFilterColumn === 'all' ? 'select-column-default' : ''}`}
                          value={requestFilterColumn}
                          onChange={(e) => setRequestFilterColumn(e.target.value)}
                          aria-label="Search in"
                        >
                          <option value="all" className="select-column-option">All columns</option>
                          <option value="username">Username</option>
                          <option value="mobileNo">Phone Number</option>
                          <option value="emailId">Email ID</option>
                          <option value="company">Company</option>
                        </select>
                        <div className="search-input-wrapper">
                          <input
                            type="search"
                            className="search-input"
                            placeholder={requestFilterColumn === 'all' ? 'Search username, phone, email, company…' : requestFilterColumn === 'username' ? 'Search by username…' : requestFilterColumn === 'mobileNo' ? 'Search by phone…' : requestFilterColumn === 'emailId' ? 'Search by email…' : 'Search by company…'}
                            value={requestSearch}
                            onChange={(e) => setRequestSearch(e.target.value)}
                            aria-label="Search access requests"
                          />
                      </div>
                      </div>
                      {requestSearch && (
                        <span className="search-result-count">
                          {customerRequests.length} {customerRequests.length === 1 ? 'result' : 'results'}
                        </span>
                      )}
                    </div>
                  </div>
                  {customerRequests.length > 0 ? (
                    <div className="ram-requests-table-wrap">
                      <div className="ram-table-wrapper">
                        <table className="ram-users-table ram-requests-table">
                          <thead>
                            <tr>
                              <th className="col-username">Username</th>
                              <th className="col-mobile">Mobile</th>
                              <th className="col-email">Email</th>
                              <th className="col-company">Company</th>
                              <th className="col-actions">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {customerRequests.map((request) => (
                              <tr key={request.id}>
                                <td className="col-username">{request.username}</td>
                                <td className="col-mobile">{request.mobileNo}</td>
                                <td className="col-email">{request.emailId || 'N/A'}</td>
                                <td className="col-company">{request.company || 'N/A'}</td>
                                <td className="col-actions">
                                  <div className="ram-request-action-buttons">
                                    <button
                                      type="button"
                                      className="ram-approve-button"
                                      onClick={() => handleApproveRequest(request.id)}
                                      disabled={processingRequestId === request.id}
                                    >
                                      {processingRequestId === request.id ? 'Processing…' : 'Approve'}
                                    </button>
                                    <button
                                      type="button"
                                      className="ram-reject-button"
                                      onClick={() => handleRejectRequest(request.id)}
                                      disabled={processingRequestId === request.id}
                                    >
                                      Reject
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ) : (
                    <div className="ram-empty-state">
                      <p>{requestSearch ? `No requests found matching "${requestSearch}".` : 'No pending customer access requests.'}</p>
                    </div>
                  )}
                </div>

                {/* Customers list – same table UI as Access Requests */}
                <div className="ram-requests-section ram-customers-section">
                  <div className="ram-table-header">
                    <h3>Customers</h3>
                    <div className="ram-search-container">
                      <div className="ram-search-bar">
                        <select
                          className={`search-filter-select ${customerFilterColumn === 'all' ? 'select-column-default' : ''}`}
                          value={customerFilterColumn}
                          onChange={(e) => setCustomerFilterColumn(e.target.value)}
                          aria-label="Search in"
                        >
                          <option value="all" className="select-column-option">All columns</option>
                          <option value="username">Username</option>
                          <option value="mobileNo">Phone Number</option>
                          <option value="emailId">Email</option>
                          <option value="company">Company</option>
                        </select>
                        <div className="search-input-wrapper">
                          <input
                            type="search"
                            className="search-input"
                            placeholder={customerFilterColumn === 'all' ? 'Search username, phone, email, company…' : customerFilterColumn === 'username' ? 'Search by username…' : customerFilterColumn === 'mobileNo' ? 'Search by phone…' : customerFilterColumn === 'emailId' ? 'Search by email…' : 'Search by company…'}
                            value={customerSearch}
                            onChange={(e) => setCustomerSearch(e.target.value)}
                            aria-label="Search customers"
                          />
                      </div>
                      </div>
                      {customerSearch && (
                        <span className="search-result-count">
                          {customers.length} {customers.length === 1 ? 'result' : 'results'}
                        </span>
                      )}
                    </div>
                  </div>
                  {customers.length > 0 ? (
                    <div className="ram-requests-table-wrap">
                      <div className="ram-table-wrapper">
                        <table className="ram-users-table ram-requests-table">
                          <thead>
                            <tr>
                              <th className="col-username">Username</th>
                              <th className="col-mobile">Mobile</th>
                              <th className="col-email">Email</th>
                              <th className="col-company">Company</th>
                              <th className="col-status">Status</th>
                              <th className="col-actions">Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {customers.map((customer) => (
                              <tr key={customer.id}>
                                <td className="col-username">{customer.username}</td>
                                <td className="col-mobile">{customer.mobileNo}</td>
                                <td className="col-email">{customer.emailId || 'N/A'}</td>
                                <td className="col-company">{customer.company || 'N/A'}</td>
                                <td className="col-status">
                                  <span className={`status-badge ${(customer.status || 'ACTIVE').toLowerCase()}`}>
                                    {customer.status || 'ACTIVE'}
                                  </span>
                                </td>
                                <td className="col-actions">
                                  <div className="ram-request-action-buttons">
                                    <button
                                      type="button"
                                      className="ram-delete-button"
                                      onClick={() => handleDeleteUser(customer.id)}
                                      title="Delete Customer"
                                    >
                                      Delete
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ) : (
                    <div className="ram-empty-state">
                      <p>{customerSearch ? `No customers found matching "${customerSearch}".` : 'No customers found.'}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Admins Tab (Visible to SUPERADMIN and ADMIN) */}
            {activeTab === 'admins' && canViewAdmins && (
              <div className="ram-tab-panel">
                <div className="ram-section-header">
                  <h2>Manage Admins</h2>
                  {isSuperAdmin && (
                    <button
                      type="button"
                      className="ram-add-button"
                      onClick={() => {
                        setShowAddForm(!showAddForm);
                        setAddUserError('');
                      }}
                    >
                      {showAddForm ? 'Cancel' : '+ Add Admin'}
                    </button>
                  )}
                </div>

                {showAddForm && isSuperAdmin && (
                  <div className="ram-add-form-wrap">
                  <div className="ram-form-card">
                    <div className="ram-form-card-header">
                      <div className="ram-form-icon">
                        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                          <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><line x1="19" y1="8" x2="19" y2="14"/><line x1="22" y1="11" x2="16" y1="11"/>
                        </svg>
                      </div>
                      <h3>Add New Admin</h3>
                      <p className="ram-form-subtitle">Create a new admin account with username, mobile and email.</p>
                    </div>
                    <form className="ram-form" onSubmit={handleAddUser}>
                      <div className="form-group">
                        <label htmlFor="admin-username">Username <span className="required">*</span></label>
                        <input
                          type="text"
                          id="admin-username"
                          name="username"
                          value={formData.username}
                          onChange={(e) => {
                            handleInputChange(e);
                            setAddUserError('');
                          }}
                          required
                          placeholder="Enter username"
                          disabled={isAddingUser}
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="admin-mobileNo">Mobile Number <span className="required">*</span></label>
                        <input
                          type="tel"
                          id="admin-mobileNo"
                          name="mobileNo"
                          value={formData.mobileNo}
                          onChange={(e) => {
                            handleInputChange(e);
                            setAddUserError('');
                          }}
                          required
                          placeholder="+919876543210"
                          disabled={isAddingUser}
                          minLength={13}
                        />
                        <span className="ram-form-hint">Format: +91 followed by 10-digit mobile number</span>
                      </div>
                      <div className="form-group">
                        <label htmlFor="admin-emailId">Email ID <span className="required">*</span></label>
                        <input
                          type="email"
                          id="admin-emailId"
                          name="emailId"
                          value={formData.emailId}
                          onChange={(e) => {
                            handleInputChange(e);
                            setAddUserError('');
                          }}
                          required
                          placeholder="user@example.com"
                          disabled={isAddingUser}
                        />
                        {emailError && <span className="ram-form-hint ram-form-hint-error">{emailError}</span>}
                      </div>
                      <div className="form-actions">
                        <button type="submit" className="ram-submit-button" disabled={isAddingUser}>
                          {isAddingUser ? 'Adding...' : 'Add Admin'}
                        </button>
                        <button
                          type="button"
                          className="ram-cancel-button"
                          onClick={() => {
                            setShowAddForm(false);
                            setFormData({ username: '', mobileNo: '+91', emailId: '' });
                            setAddUserError('');
                            setEmailError('');
                          }}
                          disabled={isAddingUser}
                        >
                          Cancel
                        </button>
                      </div>
                      {addUserError && (
                        <div className="ram-error-message">
                          <span className="message-icon">✕</span>
                          <span>{addUserError}</span>
                        </div>
                      )}
                    </form>
                  </div>
                  </div>
                )}

                <div className="ram-table-section">
                  <div className="ram-table-header">
                    <h3>Admins</h3>
                    <div className="ram-search-container">
                      <div className="ram-search-bar">
                        <select
                          className={`search-filter-select ${adminFilterColumn === 'all' ? 'select-column-default' : ''}`}
                          value={adminFilterColumn}
                          onChange={(e) => setAdminFilterColumn(e.target.value)}
                          aria-label="Search in"
                        >
                          <option value="all" className="select-column-option">All columns</option>
                          <option value="username">Username</option>
                          <option value="mobileNo">Phone Number</option>
                          <option value="emailId">Email ID</option>
                        </select>
                        <div className="search-input-wrapper">
                          <input
                            type="search"
                            className="search-input"
                            placeholder={adminFilterColumn === 'all' ? 'Search username, phone, email…' : adminFilterColumn === 'username' ? 'Search by username…' : adminFilterColumn === 'mobileNo' ? 'Search by phone…' : 'Search by email…'}
                            value={adminSearch}
                            onChange={(e) => setAdminSearch(e.target.value)}
                            aria-label="Search admins"
                          />
                      </div>
                      </div>
                      {adminSearch && (
                        <span className="search-result-count">
                          {admins.length} {admins.length === 1 ? 'result' : 'results'}
                        </span>
                      )}
                    </div>
                  </div>
                  {admins.length > 0 ? (
                    <div className="ram-table-wrapper">
                      <table className="ram-users-table">
                        <thead>
                          <tr>
                            <th className="col-username">Username</th>
                            <th className="col-mobile">Mobile Number</th>
                            <th className="col-email">Email ID</th>
                            <th className="col-status">Status</th>
                            <th className="col-actions">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {admins.map((admin) => (
                            <tr key={admin.id}>
                              <td className="col-username">{admin.username}</td>
                              <td className="col-mobile">{admin.mobileNo}</td>
                              <td className="col-email">{admin.emailId}</td>
                              <td className="col-status">
                                <span className={`status-badge ${admin.status.toLowerCase()}`}>
                                  {admin.status}
                                </span>
                              </td>
                              <td className="col-actions">
                                <div className="action-buttons">
                                  <button
                                    className="ram-delete-button"
                                    onClick={() => handleDeleteUser(admin.id)}
                                    disabled={!isSuperAdmin}
                                    title={isSuperAdmin ? "Delete Admin" : "Only superadmin has access to remove admin access"}
                                  >
                                    Delete
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="ram-empty-state">
                      <p>{adminSearch ? `No admins found matching "${adminSearch}".` : 'No admins found. Add your first admin above.'}</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {requestToast.open && (
        <div className="ram-toast-container" role="status" aria-live="polite">
          <div className={`ram-toast ram-toast--${requestToast.kind}`}>
            <span className="ram-toast-icon" aria-hidden="true">
              {requestToast.kind === 'success' ? (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 6L9 17l-5-5" />
                </svg>
              ) : (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
              )}
            </span>
            <p className="ram-toast-message">{requestToast.message}</p>
            <button
              type="button"
              className="ram-toast-close"
              onClick={() => setRequestToast((prev) => ({ ...prev, open: false }))}
              aria-label="Close notification"
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && userToDelete && (
        <div className="ram-modal-overlay" onClick={cancelDeleteUser}>
          <div className="ram-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="ram-modal-header">
              <h2>Confirm Removal</h2>
              <button type="button" className="ram-modal-close" onClick={cancelDeleteUser} disabled={isDeletingUser} aria-label="Close">×</button>
            </div>
            <div className="ram-modal-body">
              {deleteSuccessMessage ? (
                <div className="ram-success-message">{deleteSuccessMessage}</div>
              ) : (
                <>
                  <p className="ram-modal-p">
                    Are you sure you want to remove{' '}
                    {userToDelete.roleType === 'admins'
                      ? 'Admin'
                      : userToDelete.roleType === 'customers'
                        ? 'Customer'
                        : 'Engineer'}{' '}
                    access for this user?
                  </p>
                  <div className="ram-user-details-card">
                    <div className="ram-detail-row"><strong>Username</strong><span>{userToDelete.username}</span></div>
                    <div className="ram-detail-row"><strong>Mobile</strong><span>{userToDelete.mobileNo}</span></div>
                    <div className="ram-detail-row"><strong>Email</strong><span>{userToDelete.emailId || 'N/A'}</span></div>
                    {userToDelete.roleType === 'customers' && (
                      <div className="ram-detail-row"><strong>Company</strong><span>{userToDelete.company || 'N/A'}</span></div>
                    )}
                  </div>
                  {deleteError && (
                    <div className="ram-error-message">
                      <span className="message-icon">✕</span>
                      <span>{deleteError}</span>
                    </div>
                  )}
                </>
              )}
            </div>
            {!deleteSuccessMessage && (
              <div className="ram-modal-actions">
                <button type="button" className="ram-cancel-button" onClick={cancelDeleteUser} disabled={isDeletingUser}>Cancel</button>
                <button type="button" className="ram-confirm-delete-button" onClick={confirmDeleteUser} disabled={isDeletingUser}>
                  {isDeletingUser ? 'Removing...' : 'Confirm Remove'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default RoleAccessManagement;
